import { useState } from "react";
import { Plus, Trash2, Sparkles, X } from "lucide-react";
import toast from "react-hot-toast";
import { axiosInstance } from "../../lib/axios";
import type { Milestone } from "../../store/useProjectStore";

// Milestones are the billing backbone: every milestone REQUIRES title,
// description, deliverables, duration and value (the backend enforces it too),
// and its Invoice record is created automatically on save. Financial status is
// invoice-driven, so there is NO status control here — the invoice module owns it.

interface MilestonesEditorProps {
  milestones: Milestone[];
  onChange: (milestones: Milestone[]) => void;
  // Project context so "AI Enhance" writes milestone details that match the project.
  projectContext?: { name?: string; description?: string; category?: string };
}

const emptyRow = (): Milestone =>
  ({ title: "", description: "", amount: 0, status: "Pending", dueDate: null, notes: "", duration: "", deliverables: [] } as any);

const MilestonesEditor = ({ milestones, onChange, projectContext }: MilestonesEditorProps) => {
  const [aiBusy, setAiBusy] = useState<number | null>(null);
  const [delDraft, setDelDraft] = useState<Record<number, string>>({});

  const addRow = () => onChange([...milestones, emptyRow()]);
  const updateRow = (i: number, patch: Partial<Milestone>) =>
    onChange(milestones.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const removeRow = (i: number) => onChange(milestones.filter((_, idx) => idx !== i));

  const deliverablesOf = (m: any): { title: string }[] =>
    (m.deliverables || []).map((d: any) => (typeof d === "string" ? { title: d } : d));

  const addDeliverable = (i: number) => {
    const text = (delDraft[i] || "").trim();
    if (!text) return;
    updateRow(i, { deliverables: [...deliverablesOf(milestones[i]), { title: text }] } as any);
    setDelDraft((d) => ({ ...d, [i]: "" }));
  };
  const removeDeliverable = (i: number, di: number) =>
    updateRow(i, { deliverables: deliverablesOf(milestones[i]).filter((_, x) => x !== di) } as any);

  // AI expands description + deliverables + duration from the project context.
  const enhance = async (i: number) => {
    const m: any = milestones[i];
    if (!m.title?.trim() && !m.description?.trim()) {
      toast.error("Give the milestone a title first, then Enhance");
      return;
    }
    setAiBusy(i);
    try {
      const { data } = await axiosInstance.post("/project/enhance-milestone", {
        projectName: projectContext?.name || "",
        projectDescription: projectContext?.description || "",
        category: projectContext?.category || "",
        title: m.title || "",
        description: m.description || "",
        deliverables: deliverablesOf(m).map((d) => d.title),
        amount: m.amount || 0,
      });
      updateRow(i, {
        description: data.description || m.description,
        deliverables: (data.deliverables || []).map((t: string) => ({ title: t })),
        duration: data.duration || (m as any).duration,
      } as any);
      toast.success("Milestone details enhanced — review & edit freely");
    } catch (e: any) {
      toast.error(e?.response?.data?.message || "Couldn't enhance the milestone");
    } finally {
      setAiBusy(null);
    }
  };

  const total = milestones.reduce((s, m) => s + (Number(m.amount) || 0), 0);
  const incomplete = (m: any) =>
    !m.title?.trim() || !m.description?.trim() || !deliverablesOf(m).some((d) => d.title?.trim()) ||
    !(m.duration || "").trim() || !(Number(m.amount) > 0);

  return (
    <div className="pt-4 border-t border-gray-100">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider">Milestones</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            One list for delivery and billing: title, description, deliverables, duration &amp; value. Its invoice is
            created automatically, and each milestone then tracks two states on its own — <strong>work status</strong>{" "}
            (from the delivery team) and <strong>payment status</strong> (from its invoice).
          </p>
        </div>
        <button
          type="button"
          onClick={addRow}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#2462b2] to-[#1e55a0] text-white hover:opacity-95 transition"
        >
          <Plus size={14} /> Add Milestone
        </button>
      </div>

      {milestones.length === 0 ? (
        <div className="p-4 rounded-xl border-2 border-dashed border-gray-200 text-center text-xs text-gray-400 italic">
          No milestones yet. Add one to break the project into billable stages.
        </div>
      ) : (
        <div className="space-y-2">
          {milestones.map((m: any, i) => (
            <div key={i} className={`p-3 rounded-xl border bg-gray-50/50 space-y-2 ${incomplete(m) ? "border-amber-300" : "border-gray-200"}`}>
              <div className="flex items-center gap-2">
                <span className="shrink-0 w-6 h-6 grid place-items-center rounded-full bg-[#2462b2] text-white text-[11px] font-bold">{i + 1}</span>
                <input
                  placeholder="Milestone title * (e.g. Wireframes)"
                  className="flex-1 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white"
                  value={m.title}
                  onChange={(e) => updateRow(i, { title: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => enhance(i)}
                  disabled={aiBusy === i}
                  title="AI writes the description, deliverables & duration from the project context — fully editable after"
                  className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold rounded-md text-[#2462b2] bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                >
                  <Sparkles size={12} className={aiBusy === i ? "animate-pulse" : ""} />
                  {aiBusy === i ? "Enhancing…" : "AI Enhance"}
                </button>
                <button type="button" onClick={() => removeRow(i)} className="shrink-0 p-1.5 rounded-md border border-gray-200 text-red-500 hover:bg-red-50">
                  <Trash2 size={13} />
                </button>
              </div>

              <textarea
                placeholder="Description * — what this milestone covers (goes on the invoice too)"
                rows={2}
                className="w-full px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white resize-none"
                value={m.description || ""}
                onChange={(e) => updateRow(i, { description: e.target.value })}
              />

              {/* Deliverables — chips, required */}
              <div>
                <div className="flex flex-wrap gap-1.5 mb-1.5">
                  {deliverablesOf(m).map((d, di) => (
                    <span key={di} className="inline-flex items-center gap-1 text-[11px] bg-white border border-gray-200 rounded-full pl-2 pr-1 py-0.5 text-gray-700">
                      {d.title}
                      <button type="button" onClick={() => removeDeliverable(i, di)} className="text-gray-400 hover:text-red-500"><X size={11} /></button>
                    </span>
                  ))}
                  {!deliverablesOf(m).length && <span className="text-[11px] text-amber-600">Deliverables required *</span>}
                </div>
                <div className="flex gap-1.5">
                  <input
                    placeholder="Add a deliverable and press Enter (e.g. Homepage design)"
                    className="flex-1 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white"
                    value={delDraft[i] || ""}
                    onChange={(e) => setDelDraft((d) => ({ ...d, [i]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addDeliverable(i); } }}
                  />
                  <button type="button" onClick={() => addDeliverable(i)} className="px-2.5 py-1.5 text-[11px] font-semibold rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100">Add</button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="number"
                  placeholder="$ Value *"
                  className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white text-right"
                  value={m.amount}
                  onChange={(e) => updateRow(i, { amount: Number(e.target.value) || 0 })}
                />
                <input
                  placeholder="Duration * (e.g. 2 weeks)"
                  className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white"
                  value={m.duration || ""}
                  onChange={(e) => updateRow(i, { duration: e.target.value } as any)}
                />
                <input
                  type="date"
                  className="flex-1 min-w-0 px-2 py-1.5 border border-gray-200 rounded-md text-xs bg-white"
                  value={m.dueDate ? String(m.dueDate).split("T")[0] : ""}
                  onChange={(e) => updateRow(i, { dueDate: e.target.value || null })}
                />
                {/* Both states are read-only here: work status comes from the
                    delivery team (ERP), payment status from the invoice. */}
                {m.lifecycleStatus && m.lifecycleStatus !== "Backlog / Not Started" && (
                  <span className="shrink-0 px-2 py-1 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700" title="Work status — updated by the delivery team">
                    {m.lifecycleStatus}
                  </span>
                )}
                {m.paymentState && m.paymentState !== "Pending" && (
                  <span className="shrink-0 px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700" title="Payment status — follows this milestone's invoice">
                    {m.paymentState}
                  </span>
                )}
              </div>
            </div>
          ))}

          <div className="p-2.5 rounded-xl bg-blue-50 border border-blue-200 mt-3">
            <p className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Total</p>
            <p className="text-sm font-extrabold text-blue-900">${total.toFixed(2)}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MilestonesEditor;
